// Sidebar navigation data model + path helpers. Extracted from
// Sidebar.jsx to keep the component focused on rendering.
import {
  LayoutDashboard, Settings2, BarChart3, Upload, FolderOpen, Activity, Layers,
  GitBranch, BookOpen, GitCompare, Shapes, Trophy, Scale, Target, Calculator,
  LineChart, PieChart, Settings, HelpCircle, Home, Database, TrendingUp, Compass,
  Gauge, Briefcase, Award, Users, Shield, ShieldCheck, Building2, Cog, Stethoscope,
  Crosshair, Heart, HeartPulse, MailMinus, BarChartHorizontal, Shuffle,
  ClipboardCheck, ClipboardList, DollarSign, AlertTriangle, Sparkles, Library,
  MessageSquare, FileText,
} from 'lucide-react';

// Maps a route prefix to its section accent colour. Falls back to teal
// for unclassified routes. Shows as a left stripe on the active nav
// item. Keep in sync with `frontend/src/lib/sectionThemes.js`.
export const SECTION_STRIPE = {
  '/maturity-health': 'border-l-teal-400',
  '/lei-health': 'border-l-emerald-400',
  '/people-engine': 'border-l-blue-400',
  '/roe-health': 'border-l-amber-400',
  '/performance-health': 'border-l-orange-400',
  '/benchmarking': 'border-l-purple-400',
  '/admin': 'border-l-slate-400',
  '/insights': 'border-l-rose-400',
  '/re-engage': 'border-l-cyan-400',
  // Navigation Trends — indigo accent
  '/score-trends': 'border-l-indigo-400',
  '/lei-dashboard': 'border-l-indigo-400',
  '/program-analytics': 'border-l-indigo-400',
  '/results': 'border-l-indigo-400',
  '/comparison-analytics': 'border-l-indigo-400',
  '/accuracy': 'border-l-indigo-400',
  // Diagnostics — orange accent
  '/people-engine/diagnostic': 'border-l-orange-400',
  '/gap-analysis': 'border-l-orange-400',
  '/correlation-analysis': 'border-l-orange-400',
  '/anomaly-dashboard': 'border-l-orange-400',
};

export function stripeForPath(path) {
  // longest-prefix match so /lei-health/details still hits emerald.
  const match = Object.keys(SECTION_STRIPE)
    .filter((k) => path.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  return match ? SECTION_STRIPE[match] : 'border-l-teal-400';
}

export const navGroups = [
  {
    id: 'home',
    moduleId: 'home',
    label: 'Home',
    icon: Home,
    directPath: '/',
    items: [
      { path: '/', moduleId: 'home.dashboard', icon: LayoutDashboard, label: 'Home' },
    ]
  },
  {
    id: 'health-insights',
    moduleId: 'health_insights',
    label: 'Health & Insights',
    icon: HeartPulse,
    items: [
      { path: '/story-guide', moduleId: 'health.pe', icon: BookOpen, label: 'Read the Story' },
      { path: '/maturity-health', moduleId: 'health.maturity', icon: Gauge, label: 'Maturity Health' },
      { path: '/lei-health', moduleId: 'health.lei', icon: Activity, label: 'Learning Health', hint: 'LEI' },
      { path: '/people-engine', moduleId: 'health.pe', icon: Users, label: 'Capability Health', hint: 'PE' },
      { path: '/roe-health', moduleId: 'health.roe', icon: Heart, label: 'Transfer Health', hint: 'ROE' },
      { path: '/performance-health', moduleId: 'health.roe', icon: Target, label: 'Performance Health', hint: 'PH' },
      { path: '/roi-health', moduleId: 'health.roi', icon: DollarSign, label: 'Financial Health', hint: 'ROI' },
      { path: '/board-brief', moduleId: 'health.pe', icon: FileText, label: 'Board Brief' },
    ]
  },
  {
    id: 'navigation-trends',
    moduleId: 'health_insights',
    label: 'Navigation Trends',
    icon: TrendingUp,
    items: [
      { path: '/score-trends', moduleId: 'health.score_trends', icon: BarChart3, label: 'Navigation Signals' },
      { path: '/lei-dashboard', moduleId: 'health.lei_dashboard', icon: LineChart, label: 'Navigation Health', hint: 'LEI' },
      { path: '/program-analytics', moduleId: 'health.program_analytics', icon: TrendingUp, label: 'Program Navigation' },
      { path: '/results', moduleId: 'health.results', icon: BarChart3, label: 'Results & Analysis' },
      { path: '/comparison-analytics', moduleId: 'health.comparison', icon: PieChart, label: 'Comparison Analytics' },
      { path: '/accuracy', moduleId: 'health.accuracy', icon: Trophy, label: 'Accuracy Leaderboard' },
    ]
  },
  {
    id: 'diagnostics',
    moduleId: 'health_insights',
    label: 'Diagnostics',
    icon: Stethoscope,
    items: [
      { path: '/people-engine/diagnostic', moduleId: 'health.pe_diagnostic', icon: Stethoscope, label: 'Navigation Readiness', hint: 'PE' },
      { path: '/gap-analysis', moduleId: 'health.gap_analysis', icon: BarChartHorizontal, label: 'Value Conversion' },
      { path: '/correlation-analysis', moduleId: 'health.correlation', icon: Shuffle, label: 'Correlation & Divergence' },
      { path: '/anomaly-dashboard', moduleId: 'health.anomalies', icon: AlertTriangle, label: 'Anomaly Dashboard' },
    ]
  },
  {
    id: 'impact-modelling',
    moduleId: 'impact_modelling',
    label: 'Impact Modelling',
    icon: Calculator,
    items: [
      { subHeader: 'Explore' },
      { path: '/impact-modelling', moduleId: 'impact.getting_started', icon: LayoutDashboard, label: 'Getting Started' },
      { path: '/solution-finder', moduleId: 'impact.solution_finder', icon: Compass, label: 'Solution Finder' },
      { subHeader: 'Model' },
      { path: '/configure', moduleId: 'impact.single_measure', icon: Settings2, label: 'Single Measure' },
      { path: '/portfolio', moduleId: 'impact.portfolio', icon: Layers, label: 'Portfolio Analysis' },
      { path: '/matrix', moduleId: 'impact.matrix', icon: GitBranch, label: 'Full Impact Matrix' },
      { path: '/dampening-compare', moduleId: 'impact.dampening', icon: Scale, label: 'Dampening What-If' },
      { subHeader: 'Review' },
      { path: '/scenarios', moduleId: 'impact.scenarios', icon: FolderOpen, label: 'Saved Scenarios' },
      { path: '/compare', moduleId: 'impact.compare', icon: GitCompare, label: 'Compare Scenarios' },
      { path: '/roe-modeller', moduleId: 'impact.roe_modeller', icon: ClipboardCheck, label: 'Transfer Modeller', hint: 'ROE' },
    ]
  },
  {
    id: 'business-case',
    moduleId: 'business_case',
    label: 'Business Case Builder',
    icon: Briefcase,
    items: [
      { path: '/business-case-builder', moduleId: 'bcase.solution', icon: Compass, label: 'Solution Business Case' },
      { path: '/prospectus', moduleId: 'bcase.prospectus', icon: Briefcase, label: 'Custom Business Case' },
    ]
  },
  {
    id: 'benchmarking',
    moduleId: 'benchmarking',
    label: 'Benchmarking',
    icon: Award,
    items: [
      { path: '/benchmarking', moduleId: 'bench.industry', icon: Building2, label: 'Industry Benchmarks' },
      { path: '/org-benchmarks', moduleId: 'bench.org', icon: Building2, label: 'Org Benchmarks', superAdminOnly: true },
      { path: '/people-engine/benchmarking', moduleId: 'bench.pe', icon: Trophy, label: 'Capability Benchmarking', hint: 'PE' },
    ]
  },
  {
    id: 'goals-planning',
    moduleId: 'goals_planning',
    label: 'Goals & Planning',
    icon: Target,
    items: [
      { path: '/people-engine/goals', moduleId: 'goals.pe', icon: Crosshair, label: 'Capability Goals', hint: 'PE' },
      { path: '/people-engine/intervention-backlog', moduleId: 'goals.intervention_backlog', icon: Target, label: 'Intervention Backlog' },
      { path: '/goals', moduleId: 'goals.progress', icon: Target, label: 'Goals & Progress' },
      { path: '/data-management', moduleId: 'goals.track_progress', icon: LineChart, label: 'Track Progress' },
    ]
  },
  {
    id: 'data-management',
    moduleId: 'data_mgmt',
    label: 'Data Management',
    icon: Settings,
    items: [
      { path: '/data-hub', moduleId: 'data.hub', icon: Database, label: 'Data Hub' },
      { path: '/import', moduleId: 'data.model_setup', icon: Upload, label: 'Model Setup' },
      { path: '/component-data-import', moduleId: 'data.component_import', icon: Calculator, label: 'Component Import' },
      { path: '/segments', moduleId: 'data.segments', icon: Layers, label: 'Cohorts & Programs' },
      { path: '/custom-measures', moduleId: 'data.custom_measures', icon: Shapes, label: 'Custom Measures' },
    ]
  },
  {
    id: 'reference',
    moduleId: 'reference',
    label: 'Reference',
    icon: HelpCircle,
    items: [
      { path: '/lei-reference', moduleId: 'ref.lei', icon: BookOpen, label: 'LEI Measures Guide' },
      { path: '/people-engine/reference', moduleId: 'ref.pe', icon: Cog, label: 'PE Framework' },
      { path: '/roe-reference', moduleId: 'ref.roe', icon: Target, label: 'ROE Reference' },
      { path: '/maturity-reference', moduleId: 'ref.maturity', icon: Layers, label: 'Maturity Reference' },
      { path: '/benchmarks-reference', moduleId: 'ref.benchmarks', icon: BookOpen, label: 'Benchmarks Reference' },
      { path: '/transfer-methodology', moduleId: 'ref.transfer', icon: Scale, label: 'Transfer Methodology' },
      { path: '/research', moduleId: 'ref.research', icon: Award, label: 'Research & Evidence' },
    ]
  },
  {
    id: 'admin',
    moduleId: 'admin',
    label: 'Administration',
    icon: Shield,
    requiresAdmin: true,
    items: [
      { path: '/admin', moduleId: 'admin.org_user_mgmt', icon: ShieldCheck, label: 'Org & User Mgmt' },
      { path: '/admin/telemetry', moduleId: 'admin.telemetry', icon: Gauge, label: 'Platform Telemetry' },
      { path: '/admin/data-health', moduleId: 'admin.data_health', icon: HeartPulse, label: 'Data Health', superAdminOnly: true },
      { path: '/admin/code-health', moduleId: 'admin.code_health', icon: Stethoscope, label: 'Code Health', superAdminOnly: true },
      { path: '/admin/email-suppressions', moduleId: 'admin.email_suppressions', icon: MailMinus, label: 'Email Suppressions', superAdminOnly: true },
      { path: '/admin/release-notes', moduleId: 'admin.release_notes', icon: Sparkles, label: 'Release Notes', superAdminOnly: true },
      { path: '/admin/collateral', moduleId: 'admin.collateral', icon: Library, label: 'Collateral Library', superAdminOnly: true },
      { path: '/admin/industry-library', moduleId: 'admin.industry_library', icon: Layers, label: 'Industry Library', superAdminOnly: true },
      { path: '/admin/reports/builder', moduleId: 'admin.report_builder', icon: Sparkles, label: 'AI Report Builder' },
      { path: '/admin/glossary', moduleId: 'admin.glossary', icon: BookOpen, label: 'AI Glossary', superAdminOnly: true },
      { path: '/admin/messaging', moduleId: 'admin.messaging', icon: MessageSquare, label: 'Messaging Layer', superAdminOnly: true },
      { path: '/usage-analytics', moduleId: 'admin.usage_analytics', icon: Activity, label: 'Usage Analytics' },
    ]
  },
];

// Map a path to its parent group id.
export function getGroupForPath(pathname) {
  for (const group of navGroups) {
    for (const item of group.items) {
      if (!item.path) continue; // sub-header markers have no route
      if (item.path === pathname) return group.id;
      if (pathname.startsWith(item.path) && item.path !== '/') return group.id;
    }
  }
  return 'home';
}
